import { useCallback, useEffect, useRef, useState } from 'react';

const SESSION_KEY = 'vb_guest_popup_seen';
const COOLDOWN_KEY = 'vb_guest_popup_seen_at';

function isPopupBlocked(popup) {
  const cooldownHours = Number(popup?.cooldownHours) || 0;
  if (cooldownHours > 0) {
    const seenAt = localStorage.getItem(COOLDOWN_KEY);
    if (!seenAt) return false;
    const elapsed = Date.now() - Number(seenAt);
    return elapsed < cooldownHours * 3600 * 1000;
  }
  return Boolean(sessionStorage.getItem(SESSION_KEY));
}

function markPopupSeen(popup) {
  const cooldownHours = Number(popup?.cooldownHours) || 0;
  if (cooldownHours > 0) {
    localStorage.setItem(COOLDOWN_KEY, String(Date.now()));
    return;
  }
  sessionStorage.setItem(SESSION_KEY, '1');
}

export function useGuestWelcomePopup({
  enabled,
  popup,
  user,
  authLoading,
  contentLoaded,
  blocked,
}) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef(null);
  const shownRef = useRef(false);

  const dismiss = useCallback(() => {
    if (popup) markPopupSeen(popup);
    setVisible(false);
  }, [popup]);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!enabled || user || authLoading || blocked || !contentLoaded || !popup?.active) {
      return undefined;
    }

    if (isPopupBlocked(popup)) return undefined;

    const delayMs = Math.max(0, (popup.delaySeconds ?? 1) * 1000);
    timerRef.current = window.setTimeout(() => {
      if (isPopupBlocked(popup)) return;
      shownRef.current = true;
      setVisible(true);
    }, delayMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, user, authLoading, blocked, contentLoaded, popup]);

  useEffect(() => {
    if (blocked && visible && !shownRef.current) {
      setVisible(false);
    }
  }, [blocked, visible]);

  useEffect(() => {
    if (user && visible) dismiss();
  }, [user, visible, dismiss]);

  return { visible, dismiss };
}
